// ./games/crash/ui/components/AutoPlayModal.tsx

import React, {
  useState,
  useMemo,
  useCallback, // <-- HOOK 3
  useEffect, // <-- HOOK 4
  useRef,
} from 'react';
import { createPortal } from 'react-dom';
import {
  resolveAutoPlayModalStyles,
  type AutoPlayModalStyles,
} from '../styles/AutoPlay.styles';
import {
  resolveBetPanelInputVisual,
  type AutoPlayModalBehaviorConfig,
} from '@core/gameengine/controller/BaseGameController';
import {
  getWheelDeltaY,
  handleGameSurfaceWheel,
} from '../utils/autoplayModalScroll';
import { isSkyxNewRoute } from '@core/utils/gameRouteSlugs';
import { getGameFromUrl } from '@core/utils/urlParser';
import { resolveGameStoreHook } from '@core/gameengine/state/activeGameStore';
import { WalletBalance } from '@core/UI/wallet/WalletBalance';
import { getImageAsset } from '../utils/getAssetHelper';
import {
  useT,
  useNumberFormat,
  truncateToDecimals,
  formatCurrencyUnit,
} from '@core/i18n';
import type { BetSide } from '@platform/contracts';
import { useLongPress } from '../hooks/useLongPress';
import { DEFAULT_GAME_BET_LIMITS } from '@core/gameengine/config/GameBetLimits';
import {
  clampAutoplayStopAmount,
  DEFAULT_AUTOPLAY_STOP_BASE_VALUES,
  decrementAutoplayStopAmount,
  formatAutoplayStopInputDisplay,
  incrementAutoplayStopAmount,
  isAutoplayStopDecreaseDisabled,
  isAutoplayStopIncreaseDisabled,
} from '../../../core/gameengine/config/autoplayStopAmounts';

const stepButtonActiveStyle: React.CSSProperties = {
  transform: 'translateY(1px)',
  boxShadow: 'inset 0 0 0 2px rgba(0,0,0,0.08)',
};

// ──────────────────────────────────────────────────────────────
// Interfaces
export interface AutoPlaySettings {
  rounds: number | null;
  stopOnCashDecrease: number | null;
  stopOnCashIncrease: number | null;
  stopOnSingleWin: number | null;
}

interface AutoPlayModalProps {
  isOpen: boolean;
  onClose: () => void;
  onStart: (settings: AutoPlaySettings) => void;
  currency: string;
  controller?: any; // Optional controller for game-specific assets
  autoPlaySide?: BetSide | null;
}

// ──────────────────────────────────────────────────────────────
// NumberInput Component
const NumberInput: React.FC<{
  value: number | null;
  onChange: (value: number | null) => void;
  currency: string;
  disabled?: boolean;
  controller?: any;
  onActivate?: () => void;
  minValue?: number;
  maxValue?: number;
  step?: number;
  compactLayout?: boolean;
  useStopAmountSteps?: boolean;
  stopAmountBaseValues?: readonly number[];
}> = ({
  value,
  onChange,
  currency,
  disabled = false,
  controller,
  onActivate,
  minValue = 0,
  maxValue = Number.MAX_SAFE_INTEGER,
  step: stepProp,
  compactLayout = false,
  useStopAmountSteps = false,
  stopAmountBaseValues = DEFAULT_AUTOPLAY_STOP_BASE_VALUES,
}) => {
  const gameSlug = controller ? controller.getGameSlug() : 'crash';
  const step = stepProp ?? DEFAULT_GAME_BET_LIMITS.stakeStep;
  const numberFormat = useNumberFormat(currency);
  const { decimal } = numberFormat.separators;
  const styles = useMemo(
    () =>
      resolveAutoPlayModalStyles(
        gameSlug,
        controller?.getPlatformUIConfig?.()?.resolveAutoPlayModalStyles
      ),
    [controller, gameSlug]
  );
  const [localValue, setLocalValue] = useState<string>('');
  const [isEditing, setIsEditing] = useState(false);
  const [isMinusActive, setIsMinusActive] = useState(false);
  const [isPlusActive, setIsPlusActive] = useState(false);
  const valueRef = useRef(value);
  valueRef.current = value;
  const inputRef = useRef<HTMLInputElement>(null);
  const focusScrollTimeoutRef = useRef<number | null>(null);

  const clampAmount = useCallback(
    (amount: number) => clampAutoplayStopAmount(amount, maxValue, minValue),
    [maxValue, minValue]
  );

  const numericValue = truncateToDecimals(value ?? minValue);
  const formatStopDisplay = useCallback(
    (amount: number) =>
      useStopAmountSteps
        ? formatAutoplayStopInputDisplay(
            amount,
            numberFormat.formatEditableAmount
          )
        : numberFormat.formatEditableAmount(amount),
    [numberFormat, useStopAmountSteps]
  );
  const isDecreaseDisabled = useStopAmountSteps
    ? disabled ||
      isAutoplayStopDecreaseDisabled(
        numericValue,
        maxValue,
        minValue,
        stopAmountBaseValues
      )
    : disabled || numericValue <= truncateToDecimals(minValue);
  const isIncreaseDisabled = useStopAmountSteps
    ? disabled ||
      isAutoplayStopIncreaseDisabled(
        numericValue,
        maxValue,
        minValue,
        stopAmountBaseValues
      )
    : disabled || numericValue >= truncateToDecimals(maxValue);

  const stakeCounterLoopSound =
    controller?.getPlatformUIConfig?.()?.stakeCounterLoopSound;
  const activeStepperRef = useRef<'increase' | 'decrease' | null>(null);

  const stopCounterLoop = useCallback(() => {
    activeStepperRef.current = null;
    if (!stakeCounterLoopSound) return;
    controller?.getAudio?.()?.stopLoop(stakeCounterLoopSound);
  }, [controller, stakeCounterLoopSound]);

  const tryStartCounterLoop = useCallback(
    (direction: 'increase' | 'decrease', isStepDisabled: boolean) => {
      if (!stakeCounterLoopSound || isStepDisabled) return;
      const audio = controller?.getAudio?.();
      if (!audio || audio.isSfxMuted()) return;
      activeStepperRef.current = direction;
      audio.noteUserGesture();
      audio.playLoop(stakeCounterLoopSound);
    },
    [controller, stakeCounterLoopSound]
  );

  const t = useT(gameSlug);
  const zeroPlaceholder = numberFormat.formatEditableAmount(0);
  const autoplayPlusImageUrl = controller
    ? controller.getAssetUrl(getImageAsset(gameSlug, 'autoplayPlusActive'))
    : '';
  const autoplayMinusImageUrl = controller
    ? controller.getAssetUrl(getImageAsset(gameSlug, 'autoplayMinusActive'))
    : '';

  useEffect(() => {
    if (!isEditing) {
      setLocalValue(value !== null ? formatStopDisplay(value) : '');
    }
  }, [value, isEditing, formatStopDisplay]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (disabled) return;
    onActivate?.();
    const accepted = numberFormat.acceptLocalizedNumberInput(e.target.value);
    if (accepted === null) {
      return;
    }
    setLocalValue(accepted);
    setIsEditing(true);
  };

  const handleBlur = () => {
    const trimmedValue = localValue.trim();

    if (
      trimmedValue === '' ||
      trimmedValue === decimal ||
      trimmedValue === '.'
    ) {
      if (isSkyxInput) {
        onChange(0);
        setLocalValue(formatStopDisplay(0));
      } else {
        onChange(null);
        setLocalValue('');
      }
    } else {
      const num = numberFormat.parseLocalizedNumber(trimmedValue);
      if (num !== null) {
        const truncatedValue = clampAmount(truncateToDecimals(num));
        onChange(truncatedValue);
        setLocalValue(formatStopDisplay(truncatedValue));
      } else if (value !== null) {
        setLocalValue(
          formatStopDisplay(clampAmount(truncateToDecimals(value)))
        );
      } else {
        onChange(null);
        setLocalValue('');
      }
    }
    setIsEditing(false);
  };

  const handleFocus = () => {
    onActivate?.();
    setIsEditing(true);
    if (value !== null) {
      if (isSkyxInput && value === 0) {
        setLocalValue('');
      } else {
        setLocalValue(formatStopDisplay(value));
      }
    }
    if (isSkyxInput) {
      if (focusScrollTimeoutRef.current) {
        window.clearTimeout(focusScrollTimeoutRef.current);
      }
      // Delayed so the on-screen keyboard's own resize/animation has settled
      // before we scroll — doing it immediately fights that animation and is
      // what caused the visible jerk on real devices.
      focusScrollTimeoutRef.current = window.setTimeout(() => {
        inputRef.current?.scrollIntoView({
          block: 'center',
          behavior: 'smooth',
        });
      }, 300);
    }
  };

  useEffect(
    () => () => {
      if (focusScrollTimeoutRef.current) {
        window.clearTimeout(focusScrollTimeoutRef.current);
      }
    },
    []
  );

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.currentTarget.blur();
      return;
    }
    if (e.ctrlKey || e.metaKey || e.key.length > 1) return;
    if (
      numberFormat.isLocalizedDecimalKey(e.key) &&
      localValue.includes(decimal)
    ) {
      e.preventDefault();
      return;
    }
    if (!/[\d.,\s]/.test(e.key)) {
      e.preventDefault();
    }
  };

  const decreaseAmount = useCallback(() => {
    if (disabled) return;
    const current = truncateToDecimals(valueRef.current ?? minValue);
    if (useStopAmountSteps) {
      if (
        isAutoplayStopDecreaseDisabled(
          current,
          maxValue,
          minValue,
          stopAmountBaseValues
        )
      ) {
        stopCounterLoop();
        return;
      }
    } else if (current <= truncateToDecimals(minValue)) {
      stopCounterLoop();
      return;
    }
    onActivate?.();
    onChange(
      useStopAmountSteps
        ? decrementAutoplayStopAmount(
            current,
            maxValue,
            minValue,
            stopAmountBaseValues
          )
        : clampAmount(current - step)
    );
  }, [
    clampAmount,
    disabled,
    maxValue,
    minValue,
    onActivate,
    onChange,
    step,
    stopAmountBaseValues,
    stopCounterLoop,
    useStopAmountSteps,
  ]);

  const increaseAmount = useCallback(() => {
    if (disabled) return;
    const current = truncateToDecimals(valueRef.current ?? minValue);
    if (useStopAmountSteps) {
      if (
        isAutoplayStopIncreaseDisabled(
          current,
          maxValue,
          minValue,
          stopAmountBaseValues
        )
      ) {
        stopCounterLoop();
        return;
      }
    } else if (current >= truncateToDecimals(maxValue)) {
      stopCounterLoop();
      return;
    }
    onActivate?.();
    onChange(
      useStopAmountSteps
        ? incrementAutoplayStopAmount(
            current,
            maxValue,
            minValue,
            stopAmountBaseValues
          )
        : clampAmount(current + step)
    );
  }, [
    clampAmount,
    disabled,
    maxValue,
    minValue,
    onActivate,
    onChange,
    step,
    stopAmountBaseValues,
    stopCounterLoop,
    useStopAmountSteps,
  ]);

  const increaseLongPressSound = useMemo(
    () =>
      stakeCounterLoopSound
        ? {
            onPressStart: () =>
              tryStartCounterLoop('increase', isIncreaseDisabled),
            onPressEnd: stopCounterLoop,
          }
        : undefined,
    [
      stakeCounterLoopSound,
      isIncreaseDisabled,
      stopCounterLoop,
      tryStartCounterLoop,
    ]
  );

  const decreaseLongPressSound = useMemo(
    () =>
      stakeCounterLoopSound
        ? {
            onPressStart: () =>
              tryStartCounterLoop('decrease', isDecreaseDisabled),
            onPressEnd: stopCounterLoop,
          }
        : undefined,
    [
      stakeCounterLoopSound,
      isDecreaseDisabled,
      stopCounterLoop,
      tryStartCounterLoop,
    ]
  );

  useEffect(() => {
    const active = activeStepperRef.current;
    if (
      (active === 'decrease' && isDecreaseDisabled) ||
      (active === 'increase' && isIncreaseDisabled)
    ) {
      stopCounterLoop();
    }
  }, [isDecreaseDisabled, isIncreaseDisabled, stopCounterLoop]);

  useEffect(() => () => stopCounterLoop(), [stopCounterLoop]);

  const decreasePressHandlers = useLongPress(
    decreaseAmount,
    100,
    0.8,
    300,
    decreaseLongPressSound
  );
  const increasePressHandlers = useLongPress(
    increaseAmount,
    100,
    0.8,
    300,
    increaseLongPressSound
  );

  const inputChrome =
    controller?.getPlatformUIConfig?.()?.betPanelAutoControlsChrome
      ?.autoCashoutInput;
  const isSkyxInput = isSkyxNewRoute(gameSlug);
  const inputVisual = inputChrome
    ? resolveBetPanelInputVisual(inputChrome, !disabled)
    : null;
  const inputBorderColor = isSkyxInput
    ? disabled
      ? (styles.stopInputDisabledBorder ?? inputVisual?.border)
      : (styles.stopAmountInputBorder ?? inputVisual?.border)
    : inputVisual?.border;
  const stepButtonStyle: React.CSSProperties = isSkyxInput
    ? {
        appearance: 'none',
        WebkitAppearance: 'none',
        background: 'linear-gradient(135deg, #6A00FF 0%, #B347FF 100%)',
        border: 'none',
        color: '#ffffff',
      }
    : {};
  const stepIconSize = isSkyxInput
    ? compactLayout
      ? '58%'
      : '64%'
    : undefined;

  const renderStepButton = (
    isDisabled: boolean,
    isActive: boolean,
    handlers: ReturnType<typeof useLongPress>,
    setActive: (active: boolean) => void,
    imageUrl: string,
    alt: string
  ) => (
    <button
      className={isSkyxInput ? 'autoplay-3d-btn' : undefined}
      style={{
        ...styles.inputButton,
        ...stepButtonStyle,
        ...(compactLayout ? styles.inputButtonMobile : {}),
        ...(inputVisual && !isSkyxInput
          ? {
              background: 'transparent',
              boxShadow: 'none',
            }
          : {}),
        // For skyx_new, the generic .autoplay-3d-btn CSS class's :active
        // rule owns the pressed look — applying the old inline
        // stepButtonActiveStyle on top would win over it (inline beats a
        // CSS class) and mask the new, more pronounced 3D press effect.
        // Other games have no .autoplay-3d-btn class at all, so they still
        // need this inline fallback.
        ...(isActive && !isDisabled && !isSkyxInput
          ? stepButtonActiveStyle
          : {}),
        ...(isSkyxInput
          ? isDisabled
            ? {
                background: 'rgba(123,60,255,0.12)',
                color: 'rgba(201,184,245,0.35)',
                cursor: 'not-allowed',
              }
            : {}
          : isDisabled
            ? { opacity: 0.5, cursor: 'default' }
            : {}),
      }}
      onMouseDown={e => {
        if (!isDisabled) {
          setActive(true);
          handlers.onMouseDown(e);
        }
      }}
      onMouseUp={() => {
        setActive(false);
        handlers.onMouseUp();
      }}
      onMouseLeave={() => {
        setActive(false);
        handlers.onMouseLeave();
      }}
      onTouchStart={e => {
        if (!isDisabled) {
          setActive(true);
          handlers.onTouchStart(e);
        }
      }}
      onTouchEnd={() => {
        setActive(false);
        handlers.onTouchEnd();
      }}
      onTouchCancel={() => {
        setActive(false);
        handlers.onTouchCancel();
      }}
      onTouchMove={handlers.onTouchMove}
      disabled={isDisabled}
    >
      <img
        src={imageUrl}
        alt={alt}
        style={{
          ...styles.iconButton,
          ...(stepIconSize
            ? { maxWidth: stepIconSize, maxHeight: stepIconSize }
            : {}),
          ...(inputVisual && !isDisabled
            ? { filter: 'brightness(0) invert(1)' }
            : isDisabled
              ? { filter: 'grayscale(1)' }
              : {}),
        }}
      />
    </button>
  );

  const inputFieldStyle: React.CSSProperties = {
    ...styles.inputField,
    ...(inputVisual
      ? {
          background: 'transparent',
          color: inputVisual.color,
          WebkitTextFillColor: inputVisual.color,
          caretColor: inputVisual.color,
          opacity: 1,
          cursor: disabled ? 'default' : 'text',
        }
      : disabled
        ? { opacity: 0.5, cursor: 'default' }
        : {}),
  };

  if (isSkyxInput) {
    // Desktop pill stays 78px so 1000.00 / 10000.00 keep 2 decimal places.
    // Don't resize while editing — a focused input's width change can jump
    // the caret on mobile.
    const stopInputMaxWidth = compactLayout ? undefined : '78px';
    const isZeroValue = isEditing
      ? (numberFormat.parseLocalizedNumber(localValue) ?? 0) === 0
      : numericValue === 0;

    return (
      <div
        style={{
          ...styles.stopAmountRow,
          ...(compactLayout ? styles.stopAmountRowMobile : {}),
        }}
      >
        {renderStepButton(
          isDecreaseDisabled,
          isMinusActive,
          decreasePressHandlers,
          setIsMinusActive,
          autoplayMinusImageUrl,
          t('ui.control_panel.bet_panel.decrease_stake')
        )}
        <div
          style={{
            ...styles.stopAmountInputShell,
            ...(compactLayout ? styles.stopAmountInputShellMobile : {}),
            background:
              styles.stopAmountInputBackground ??
              inputVisual?.background ??
              '#0e0430',
            border: `1px solid ${
              isSkyxInput && !isZeroValue
                ? 'rgba(255, 77, 216, 0.6)'
                : isSkyxInput && isEditing && styles.stopAmountInputFocusBorder
                  ? styles.stopAmountInputFocusBorder
                  : (inputBorderColor ?? inputVisual?.border ?? '#a83dff')
            }`,
            boxShadow: isSkyxInput
              ? !isZeroValue
                ? '0 0 8px rgba(255, 77, 216, 0.45)'
                : isEditing
                  ? styles.stopAmountInputFocusShadow
                  : (styles.stopAmountInputShadow ?? inputVisual?.boxShadow)
              : inputVisual?.boxShadow,
            opacity: inputVisual?.opacity ?? 1,
            transition: isSkyxInput ? 'none' : undefined,
          }}
        >
          <input
            ref={inputRef}
            type="text"
            inputMode="decimal"
            lang={numberFormat.locale}
            data-skyx-typing-sound=""
            style={{
              ...inputFieldStyle,
              width: '100%',
              minWidth: compactLayout ? '60px' : '56px',
              maxWidth: stopInputMaxWidth,
              border: 'none',
              outline: 'none',
              boxShadow: 'none',
              // Without this, some browsers draw their own native
              // hover/focus chrome (a light-mode-tinted ring) around text
              // inputs on a dark background, since nothing here otherwise
              // tells the browser this form control is meant to be dark.
              colorScheme: 'dark',
              WebkitAppearance: 'none',
              appearance: 'none',
            }}
            value={localValue}
            onChange={handleChange}
            onBlur={handleBlur}
            onFocus={handleFocus}
            onKeyDown={handleKeyDown}
            disabled={disabled}
          />
        </div>
        {renderStepButton(
          isIncreaseDisabled,
          isPlusActive,
          increasePressHandlers,
          setIsPlusActive,
          autoplayPlusImageUrl,
          t('ui.control_panel.bet_panel.increase_stake')
        )}
        <span
          style={{
            ...styles.currencyLabel,
            ...(compactLayout ? styles.currencyLabelMobile : {}),
            ...(isSkyxInput && styles.currencyLabelColor
              ? { color: styles.currencyLabelColor, opacity: 1 }
              : inputChrome
                ? {
                    color: disabled
                      ? inputChrome.multiplierColorDisabled
                      : inputChrome.multiplierColorEnabled,
                    opacity: 1,
                  }
                : disabled
                  ? { opacity: 0.5 }
                  : {}),
          }}
        >
          {formatCurrencyUnit(currency)}
        </span>
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', alignItems: 'center' }}>
      <div
        style={{
          ...styles.inputGroup,
          justifyContent: 'center',
          ...(inputVisual
            ? {
                backgroundColor: inputVisual.background,
                background: inputVisual.background,
                border: `1px solid ${inputBorderColor ?? inputVisual.border}`,
                borderRadius: '8px',
                boxShadow: inputVisual.boxShadow,
                opacity: inputVisual.opacity,
                padding: '2px 4px',
              }
            : {}),
        }}
      >
        {renderStepButton(
          isDecreaseDisabled,
          isMinusActive,
          decreasePressHandlers,
          setIsMinusActive,
          autoplayMinusImageUrl,
          t('ui.control_panel.bet_panel.decrease_stake')
        )}

        <input
          type="text"
          inputMode="decimal"
          lang={numberFormat.locale}
          data-skyx-typing-sound=""
          style={inputFieldStyle}
          value={localValue}
          onChange={handleChange}
          onBlur={handleBlur}
          onFocus={handleFocus}
          onKeyDown={handleKeyDown}
          placeholder={zeroPlaceholder}
          disabled={disabled}
        />

        {renderStepButton(
          isIncreaseDisabled,
          isPlusActive,
          increasePressHandlers,
          setIsPlusActive,
          autoplayPlusImageUrl,
          t('ui.control_panel.bet_panel.increase_stake')
        )}
      </div>

      <span
        style={{
          ...styles.currencyLabel,
          ...(inputChrome
            ? {
                color: disabled
                  ? inputChrome.multiplierColorDisabled
                  : inputChrome.multiplierColorEnabled,
                opacity: 1,
              }
            : disabled
              ? { opacity: 0.5 }
              : {}),
          marginLeft: '1px',
        }}
      >
        {formatCurrencyUnit(currency)}
      </span>
    </div>
  );
};

// ──────────────────────────────────────────────────────────────
// RoundOptionButton — a single round-count preset pill.
const RoundOptionButton: React.FC<{
  option: number;
  isActive: boolean;
  isHovered: boolean;
  isSkyx: boolean;
  isMobile: boolean;
  styles: AutoPlayModalStyles;
  onSelect: () => void;
  onHoverChange: (hovered: boolean) => void;
}> = ({
  option,
  isActive,
  isHovered: _isHovered,
  isSkyx,
  isMobile,
  styles,
  onSelect,
  onHoverChange,
}) => (
  <button
    type="button"
    aria-pressed={isActive}
    className={isSkyx ? 'autoplay-3d-btn' : undefined}
    style={{
      ...styles.roundButton,
      ...(isSkyx && isMobile ? (styles.roundButtonMobile ?? {}) : {}),
      ...(isActive
        ? {
            ...styles.roundButtonActive,
            ...(isSkyx && isMobile
              ? (styles.roundButtonActiveMobile ?? {})
              : {}),
          }
        : {}),
    }}
    onClick={onSelect}
    onMouseEnter={() => onHoverChange(true)}
    onMouseLeave={() => onHoverChange(false)}
  >
    {option}
  </button>
);

function applyAutoPlayModalDefaults(
  behavior: AutoPlayModalBehaviorConfig | undefined,
  setters: {
    setRounds: (value: number | null) => void;
    setIsRoundsActive: (value: boolean) => void;
    setIsDecreaseStop: (value: boolean) => void;
    setDecreaseValue: (value: number | null) => void;
    setIsIncreaseStop: (value: boolean) => void;
    setIncreaseValue: (value: number | null) => void;
    setIsWinStop: (value: boolean) => void;
    setWinValue: (value: number | null) => void;
  }
): void {
  if (behavior?.defaultRounds != null) {
    setters.setRounds(behavior.defaultRounds);
    setters.setIsRoundsActive(true);
  } else {
    setters.setRounds(null);
    setters.setIsRoundsActive(false);
  }

  if (behavior?.hideStopToggles) {
    const defaultAmount = behavior.defaultStopAmount ?? 0;
    setters.setIsDecreaseStop(false);
    setters.setDecreaseValue(defaultAmount);
    setters.setIsIncreaseStop(false);
    setters.setIncreaseValue(defaultAmount);
    setters.setIsWinStop(false);
    setters.setWinValue(defaultAmount);
    return;
  }

  setters.setIsDecreaseStop(false);
  setters.setDecreaseValue(null);
  setters.setIsIncreaseStop(false);
  setters.setIncreaseValue(null);
  setters.setIsWinStop(false);
  setters.setWinValue(null);
}

// ──────────────────────────────────────────────────────────────
// AutoPlayModal Component
export const AutoPlayModal: React.FC<AutoPlayModalProps> = ({
  isOpen,
  onClose,
  onStart,
  currency,
  controller,
  autoPlaySide = null,
}) => {
  // HOOKS MUST BE CALLED UNCONDITIONALLY AT THE TOP LEVEL
  const useGameStore = resolveGameStoreHook(controller);
  const gameSlug = controller?.getGameSlug?.() ?? getGameFromUrl() ?? 'crash';
  const isSkyx = isSkyxNewRoute(gameSlug);
  const t = useT(isSkyx ? 'skyx_new' : undefined);
  const insufficientBalanceMessage = t(
    'ui.popuppanel.autoplay.error_insufficient_balance'
  );

  const { deviceType, isLandscape, balance, betStake, betLimits } =
    useGameStore((s: any) => {
      const side = (autoPlaySide ?? s.autoPlayModalSide ?? 'A') as BetSide;
      return {
        deviceType: s.deviceType ?? 'desktop',
        isLandscape: s.isLandscape ?? false,
        balance: Number(s.balance ?? 0),
        betStake: Number(s.bets?.[side]?.stake ?? 0),
        betLimits: s.betLimits ?? DEFAULT_GAME_BET_LIMITS,
      };
    });

  const amountStep = betLimits.stakeStep ?? DEFAULT_GAME_BET_LIMITS.stakeStep;

  const hasInsufficientBalance =
    balance <= 0 || (betStake > 0 && balance < betStake);

  const autoPlayStyleResolver =
    controller?.getPlatformUIConfig?.()?.resolveAutoPlayModalStyles;
  const autoPlayModalBehavior =
    controller?.getPlatformUIConfig?.()?.autoPlayModalBehavior;
  const hideStopToggles = autoPlayModalBehavior?.hideStopToggles === true;
  const useStopAmountSteps = autoPlayModalBehavior?.useStopAmountSteps === true;
  const stopAmountBaseValues =
    autoPlayModalBehavior?.stopAmountBaseValues ??
    DEFAULT_AUTOPLAY_STOP_BASE_VALUES;

  const stopMinAmount = autoPlayModalBehavior?.minStopAmount ?? 0;
  const stopMaxAmountFallback = useMemo(
    () =>
      autoPlayModalBehavior?.maxStopAmount ??
      Math.max(stopMinAmount, truncateToDecimals(balance)),
    [autoPlayModalBehavior?.maxStopAmount, balance, stopMinAmount]
  );
  const stopMaxLossAmount =
    autoPlayModalBehavior?.maxStopOnCashDecrease ?? stopMaxAmountFallback;
  const stopMaxProfitAmount =
    autoPlayModalBehavior?.maxStopOnCashIncrease ?? stopMaxAmountFallback;
  const stopMaxSingleWinAmount =
    autoPlayModalBehavior?.maxStopOnSingleWin ?? stopMaxAmountFallback;

  const clampStopFieldValue = useCallback(
    (amount: number | null, maxAmount: number) => {
      if (amount === null || amount <= 0) {
        return null;
      }
      return clampAutoplayStopAmount(amount, maxAmount, stopMinAmount);
    },
    [stopMinAmount]
  );

  // 1. Get styles (Hook 1) — game themes via PlatformUIConfig, not game imports
  const styles = useMemo(
    () => resolveAutoPlayModalStyles(gameSlug, autoPlayStyleResolver),
    [gameSlug, autoPlayStyleResolver]
  );

  // 2. Hover/Pressed States (Hooks 2-9)
  const [hoveredRound, setHoveredRound] = useState<number | null>(null);
  const [hoveredReset, setHoveredReset] = useState(false);
  const [hoveredStart, setHoveredStart] = useState(false);
  const [hoveredErrorClose, setHoveredErrorClose] = useState(false);
  const [isCloseHovered, setIsCloseHovered] = useState(false);
  const [isCloseActive, setIsCloseActive] = useState(false);
  const [pressedReset, setPressedReset] = useState(false);
  const [pressedStart, setPressedStart] = useState(false);

  // 3. Ref Timers (Hooks 10-11)
  const resetPressTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const startPressTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // 4. Animation States (Hooks 12-13)
  const [shouldRender, setShouldRender] = useState(false);
  const [isAnimatingIn, setIsAnimatingIn] = useState(false);

  // Reduced-motion preference — skip the slide/fade animation entirely.
  const [prefersReducedMotion, setPrefersReducedMotion] = useState(false);
  useEffect(() => {
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
    setPrefersReducedMotion(mq.matches);
    const onChange = () => setPrefersReducedMotion(mq.matches);
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, []);

  // 5. Ref Timeout (Hook 14)
  const timeoutIdRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const overlayScrollRef = useRef<HTMLDivElement | null>(null);

  // Animation constants (match CSS transition duration)
  const ANIMATION_DURATION = prefersReducedMotion ? 0 : 300; // ms

  // 6. Animation Effect (Hook 15)
  useEffect(() => {
    if (isOpen) {
      setShouldRender(true);
      // Timeout to allow the component to render before starting animation
      timeoutIdRef.current = setTimeout(() => {
        setIsAnimatingIn(true);
      }, 10);
    } else if (shouldRender) {
      // Start fade-out animation
      setIsAnimatingIn(false);
      // Wait for animation to finish before unmounting
      timeoutIdRef.current = setTimeout(() => {
        setShouldRender(false);
      }, ANIMATION_DURATION);
    }

    return () => {
      if (timeoutIdRef.current) {
        clearTimeout(timeoutIdRef.current);
      }
    };
  }, [isOpen, shouldRender]);

  // Allow wheel over the game canvas / play board to scroll autoplay options.
  useEffect(() => {
    if (!isOpen) {
      document.body.classList.remove('autoplay-modal-open');
      return;
    }

    document.body.classList.add('autoplay-modal-open');
    return () => {
      document.body.classList.remove('autoplay-modal-open');
    };
  }, [isOpen]);

  // Baseline visual-viewport height, captured once per open before any
  // keyboard interaction — lets the two effects below tell "on-screen
  // keyboard opened" (height shrinks a lot; the browser legitimately shifts
  // visualViewport.offsetTop to scroll the focused input into view) apart
  // from "iframe scrolled relative to parent" (offset changes with no
  // matching height shrink). Without this distinction, opening the keyboard
  // was misread as iframe misalignment and forcibly scrolled back to 0
  // mid-animation — the reported background "jerk" fighting the browser's
  // own keyboard-avoidance scroll.
  const baselineViewportHeightRef = useRef<number | null>(null);
  useEffect(() => {
    if (!isOpen) {
      baselineViewportHeightRef.current = null;
      return;
    }
    if (baselineViewportHeightRef.current === null) {
      baselineViewportHeightRef.current = window.visualViewport?.height ?? null;
    }
  }, [isOpen]);

  const isKeyboardLikelyOpen = () => {
    const vv = window.visualViewport;
    const baseline = baselineViewportHeightRef.current;
    if (!vv || baseline === null) return false;
    return baseline - vv.height > 120;
  };

  // Only reset shell scroll when the iframe visual viewport is offset (misaligned
  // fixed overlay). Never wipe game scroll in normal windowed desktop — that was
  // breaking scroll after the autoplay popup opens.
  useEffect(() => {
    if (!isOpen) return;

    const resetShellScroll = () => {
      if (isKeyboardLikelyOpen()) return;
      const vv = window.visualViewport;
      const iframeMisaligned =
        !!vv && (vv.offsetTop !== 0 || vv.offsetLeft !== 0);
      if (!iframeMisaligned) {
        return;
      }

      if (typeof window !== 'undefined' && window.scrollY !== 0) {
        window.scrollTo(0, 0);
      }
      if (document.documentElement.scrollTop !== 0) {
        document.documentElement.scrollTop = 0;
      }
      if (document.body.scrollTop !== 0) {
        document.body.scrollTop = 0;
      }
    };

    resetShellScroll();
    const t1 = window.setTimeout(resetShellScroll, 50);
    const t2 = window.setTimeout(resetShellScroll, 200);

    return () => {
      window.clearTimeout(t1);
      window.clearTimeout(t2);
    };
  }, [isOpen]);

  // Keep the fixed overlay pinned to the visible iframe viewport (parent page
  // may have scrolled the iframe). Without this, clicks miss the controls.
  // Scroll itself matches history-round / PF: native overflow on contentArea.
  const [viewportBox, setViewportBox] = useState<{
    top: number;
    left: number;
    width: number;
    height: number;
  } | null>(null);

  useEffect(() => {
    if (!isOpen && !shouldRender) {
      setViewportBox(null);
      return;
    }

    const syncViewport = () => {
      const vv = window.visualViewport;
      if (!vv || isKeyboardLikelyOpen()) {
        setViewportBox(null);
        return;
      }

      // Only shift x/y when the iframe visual viewport is offset (parent scroll).
      if (vv.offsetTop === 0 && vv.offsetLeft === 0) {
        setViewportBox(null);
        return;
      }
      setViewportBox({
        top: vv.offsetTop,
        left: vv.offsetLeft,
        width: vv.width,
        height: vv.height,
      });
    };

    syncViewport();
    const vv = window.visualViewport;
    vv?.addEventListener('resize', syncViewport);
    vv?.addEventListener('scroll', syncViewport);
    window.addEventListener('resize', syncViewport);
    window.addEventListener('scroll', syncViewport, true);

    return () => {
      vv?.removeEventListener('resize', syncViewport);
      vv?.removeEventListener('scroll', syncViewport);
      window.removeEventListener('resize', syncViewport);
      window.removeEventListener('scroll', syncViewport, true);
    };
  }, [isOpen, shouldRender]);

  // Forward wheel over the game canvas into the autoplay content scroller
  // (same idea as history-round modal's bet-list-scrollbar — native there,
  // JS only when the event hits the non-scrolling Pixi surface).
  useEffect(() => {
    if (!isOpen || !shouldRender) return;

    const onWheel = (event: WheelEvent) => {
      if (event.ctrlKey) return;
      const target = event.target;
      if (
        target instanceof Element &&
        target.closest('[data-autoplay-modal="true"]')
      ) {
        // Let the modal's native overflow-y:auto handle it (like PF / history).
        return;
      }
      if (handleGameSurfaceWheel(getWheelDeltaY(event)) && event.cancelable) {
        event.preventDefault();
      }
    };

    const wheelOpts: AddEventListenerOptions = {
      passive: false,
      capture: true,
    };
    document.addEventListener('wheel', onWheel, wheelOpts);
    return () => {
      document.removeEventListener('wheel', onWheel, true);
    };
  }, [isOpen, shouldRender]);
  // 7. Settings state (Hooks 16-23)
  const [rounds, setRounds] = useState<number | null>(null);
  const [isRoundsActive, setIsRoundsActive] = useState(false);
  const [isDecreaseStop, setIsDecreaseStop] = useState(false);
  const [decreaseValue, setDecreaseValue] = useState<number | null>(null);
  const [isIncreaseStop, setIsIncreaseStop] = useState(false);
  const [increaseValue, setIncreaseValue] = useState<number | null>(null);
  const [isWinStop, setIsWinStop] = useState(false);
  const [winValue, setWinValue] = useState<number | null>(null);

  // 8. Error State (Hook 24)
  const [error, setError] = useState<string | null>(null);
  const errorBannerRef = useRef<HTMLDivElement | null>(null);

  const playAutoPlayErrorSound = useCallback(() => {
    if (!isSkyx) {
      return;
    }
    const audio = controller?.getAudio?.();
    if (!audio) {
      return;
    }
    audio.noteUserGesture();
    audio.play('cancelPopup');
  }, [controller, isSkyx]);

  const playAutoPlayStartSound = useCallback(() => {
    const audio = controller?.getAudio?.();
    if (isSkyx) {
      audio?.noteUserGesture();
      audio?.play('autoPlay');
    } else if (
      controller?.getPlatformUIConfig?.()?.useGlobalButtonClickSound !== true
    ) {
      audio?.play('bet');
    }
  }, [controller, isSkyx]);

  // Scroll the error banner into view whenever it appears — the modal's
  // content area scrolls independently, so a validation error can fire while
  // the user is scrolled down past the header and go unnoticed otherwise.
  useEffect(() => {
    if (error) {
      errorBannerRef.current?.scrollIntoView({
        behavior: 'smooth',
        block: 'nearest',
      });
    }
  }, [error]);

  // 9. Reset on open Effect (Hook 25) — fires only when the modal transitions
  // to open, not on every balance tick. hasInsufficientBalance/
  // insufficientBalanceMessage are handled by the next effect instead: since
  // balance can change live while this modal stays open, including them here
  // would wipe the rounds/stop amounts the user already entered every time
  // the balance crosses the insufficient threshold.
  useEffect(() => {
    // Only reset when it's actively opening/rendered and isOpen is true
    if (shouldRender && isOpen) {
      applyAutoPlayModalDefaults(autoPlayModalBehavior, {
        setRounds,
        setIsRoundsActive,
        setIsDecreaseStop,
        setDecreaseValue,
        setIsIncreaseStop,
        setIncreaseValue,
        setIsWinStop,
        setWinValue,
      });
      setHoveredRound(null);
      setHoveredReset(false);
      setHoveredStart(false);
      setIsCloseHovered(false);
      setIsCloseActive(false);
      if (hasInsufficientBalance) {
        setError(insufficientBalanceMessage);
        playAutoPlayErrorSound();
      } else {
        setError(null);
      }
      setPressedReset(false);
      setPressedStart(false);
    }
  }, [shouldRender, isOpen, autoPlayModalBehavior]);

  // 9b. Keep the insufficient-balance banner in sync with live balance
  // updates while the modal is open, without resetting user input.
  useEffect(() => {
    if (!shouldRender || !isOpen) return;
    setError(prev => {
      if (hasInsufficientBalance) return insufficientBalanceMessage;
      return prev === insufficientBalanceMessage ? null : prev;
    });
  }, [
    shouldRender,
    isOpen,
    hasInsufficientBalance,
    insufficientBalanceMessage,
  ]);

  // 10. getModalStyle Function (Hook 26 - must be the final hook)
  const getModalStyle = useCallback(() => {
    let baseStyle: React.CSSProperties;

    if (deviceType === 'mobile') {
      baseStyle = styles.modalMobile;
    } else if (deviceType === 'tablet') {
      baseStyle = styles.modalTablet;
    } else {
      baseStyle = styles.modal;
    }

    // Get the base horizontal centering transform, e.g., 'translateX(-50%)'
    const baseTransform =
      baseStyle.transform && baseStyle.transform !== 'none'
        ? baseStyle.transform
        : '';

    // Animate using translateY for smooth slide-in
    const startTransform = baseTransform
      ? `${baseTransform} translateY(-24px)`
      : 'translateY(-24px)';
    const endTransform = baseTransform || 'none';

    return {
      ...baseStyle,

      // Apply the animated properties:
      transform: isAnimatingIn ? endTransform : startTransform,
      opacity: isAnimatingIn ? 1 : 0,

      // Apply the smooth transition
      transition: `all ${ANIMATION_DURATION}ms cubic-bezier(0.25, 0.46, 0.45, 0.94)`,
      // The pulsing glow itself now lives in CSS (a `::before` layer
      // animating opacity, injected via styles.modalKeyframes below) —
      // animating box-shadow directly here forced a full repaint every
      // frame, which is the likely cause of caret rendering glitches in a
      // focused input elsewhere in the modal on real mobile devices.
    };
  }, [deviceType, styles, isAnimatingIn, ANIMATION_DURATION]);

  // --- Handlers (must be after all hooks) ---

  // --- Replace these handlers in AutoPlayModal ---

  const handlePressStartReset = () => {
    if (resetPressTimerRef.current) {
      clearTimeout(resetPressTimerRef.current);
    }
    // ✅ Set immediately for all devices (mobile/tablet/desktop)
    setPressedReset(true);
  };

  const handlePressEndReset = () => {
    if (resetPressTimerRef.current) {
      clearTimeout(resetPressTimerRef.current);
      resetPressTimerRef.current = null;
    }
    setPressedReset(false);
  };

  const handlePressStartStart = () => {
    if (startPressTimerRef.current) {
      clearTimeout(startPressTimerRef.current);
    }
    // ✅ Set immediately for all devices (mobile/tablet/desktop)
    setPressedStart(true);
  };

  const handlePressEndStart = () => {
    if (startPressTimerRef.current) {
      clearTimeout(startPressTimerRef.current);
      startPressTimerRef.current = null;
    }
    setPressedStart(false);
  };

  const handleOverlayClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  const handleReset = () => {
    applyAutoPlayModalDefaults(autoPlayModalBehavior, {
      setRounds,
      setIsRoundsActive,
      setIsDecreaseStop,
      setDecreaseValue,
      setIsIncreaseStop,
      setIncreaseValue,
      setIsWinStop,
      setWinValue,
    });
    setError(null);
  };

  const handleDecreaseToggle = () => {
    if (isDecreaseStop) {
      setIsDecreaseStop(false);
      setDecreaseValue(null);
    } else {
      setIsDecreaseStop(true);
      setDecreaseValue(0);
    }
  };
  const handleIncreaseToggle = () => {
    if (isIncreaseStop) {
      setIsIncreaseStop(false);
      setIncreaseValue(null);
    } else {
      setIsIncreaseStop(true);
      setIncreaseValue(0);
    }
  };
  const handleWinToggle = () => {
    if (isWinStop) {
      setIsWinStop(false);
      setWinValue(null);
    } else {
      setIsWinStop(true);
      setWinValue(0);
    }
  };

  const handleStart = () => {
    if (hasInsufficientBalance) {
      setError(insufficientBalanceMessage);
      playAutoPlayErrorSound();
      return;
    }

    const hasRounds = isRoundsActive && rounds !== null;

    if (!hasRounds) {
      setError(t('ui.popuppanel.autoplay.error_set_rounds'));
      playAutoPlayErrorSound();
      return;
    }

    const errorMessage = t('ui.popuppanel.autoplay.error_zero_stop');

    // Toggles hidden (skyx_new): every stop field is always "available", so
    // validate off the actual typed/stepped values, not the touched-state
    // flags below (isDecreaseStop/etc only flip on focus/change and would
    // otherwise let a touched-but-zero field block Start even while another
    // untouched field already holds a real value).
    if (hideStopToggles) {
      const hasDecreaseValue = decreaseValue !== null && decreaseValue > 0;
      const hasIncreaseValue = increaseValue !== null && increaseValue > 0;
      const hasWinValue = winValue !== null && winValue > 0;

      if (!hasDecreaseValue && !hasIncreaseValue && !hasWinValue) {
        setError(errorMessage);
        playAutoPlayErrorSound();
        return;
      }

      setError(null);
      playAutoPlayStartSound();
      onStart({
        rounds,
        stopOnCashDecrease: hasDecreaseValue
          ? clampStopFieldValue(decreaseValue, stopMaxLossAmount)
          : null,
        stopOnCashIncrease: hasIncreaseValue
          ? clampStopFieldValue(increaseValue, stopMaxProfitAmount)
          : null,
        stopOnSingleWin: hasWinValue
          ? clampStopFieldValue(winValue, stopMaxSingleWinAmount)
          : null,
      });
      onClose();
      return;
    }

    const anyStopActive = isDecreaseStop || isIncreaseStop || isWinStop;

    if (isDecreaseStop && (decreaseValue === null || decreaseValue <= 0)) {
      setError(errorMessage);
      playAutoPlayErrorSound();
      return;
    }

    if (isIncreaseStop && (increaseValue === null || increaseValue <= 0)) {
      setError(errorMessage);
      playAutoPlayErrorSound();
      return;
    }

    if (isWinStop && (winValue === null || winValue <= 0)) {
      setError(errorMessage);
      playAutoPlayErrorSound();
      return;
    }

    if (!autoPlayModalBehavior?.allowRoundsOnlyStart && !anyStopActive) {
      setError(t('ui.popuppanel.autoplay.error_specify_stop'));
      playAutoPlayErrorSound();
      return;
    }

    setError(null);
    playAutoPlayStartSound();
    onStart({
      rounds,
      stopOnCashDecrease: isDecreaseStop
        ? clampStopFieldValue(decreaseValue, stopMaxLossAmount)
        : null,
      stopOnCashIncrease: isIncreaseStop
        ? clampStopFieldValue(increaseValue, stopMaxProfitAmount)
        : null,
      stopOnSingleWin: isWinStop
        ? clampStopFieldValue(winValue, stopMaxSingleWinAmount)
        : null,
    });
    onClose();
  };

  const closeNormalUrl = controller
    ? controller.getAssetUrl(getImageAsset(gameSlug, 'modalCloseNormal'))
    : '';
  const closeOverUrl = controller
    ? controller.getAssetUrl(getImageAsset(gameSlug, 'modalCloseOver'))
    : '';
  const closeDownUrl = controller
    ? controller.getAssetUrl(getImageAsset(gameSlug, 'modalCloseDown'))
    : '';

  const closeButtonImgSrc = isCloseActive
    ? closeDownUrl
    : isCloseHovered
      ? closeOverUrl
      : closeNormalUrl;

  // 🐛 HOOKS FIX: Conditional return must be AFTER all hooks.
  if (!shouldRender) return null;

  const roundOptions: number[] = autoPlayModalBehavior?.roundOptions ?? [
    10, 20, 50, 100,
  ];

  const renderStopLimitSection = (
    titleKey: string,
    descriptionKey: string,
    value: number | null,
    onChange: (value: number | null) => void,
    isEnabled: boolean,
    onToggle: () => void,
    isToggleOn: boolean,
    maxAmount: number,
    onActivate?: () => void
  ) => {
    const isSkyxMobileStop = isSkyx && deviceType === 'mobile';

    if (hideStopToggles) {
      if (isSkyxMobileStop) {
        return (
          <div
            style={{
              ...styles.section,
              ...styles.sectionMobile,
            }}
          >
            <div style={styles.stopSectionMobileStack ?? {}}>
              <span
                style={styles.stopSectionTitleMobile ?? styles.stopSectionTitle}
              >
                {t(titleKey)}
              </span>
              <p
                style={
                  styles.stopSectionDescriptionBelow ??
                  styles.stopSectionDescription
                }
              >
                {t(descriptionKey)}
              </p>
              <NumberInput
                value={value}
                onChange={onChange}
                currency={currency}
                disabled={false}
                controller={controller}
                onActivate={onActivate}
                minValue={stopMinAmount}
                maxValue={maxAmount}
                step={amountStep}
                compactLayout
                useStopAmountSteps={useStopAmountSteps}
                stopAmountBaseValues={stopAmountBaseValues}
              />
            </div>
          </div>
        );
      }

      return (
        <div style={styles.section}>
          <div style={styles.stopSectionRow}>
            <div style={styles.stopSectionCopy}>
              <span style={styles.stopSectionTitle}>{t(titleKey)}</span>
              <p style={styles.stopSectionDescription}>{t(descriptionKey)}</p>
            </div>
            <div style={styles.stopSectionInput}>
              <NumberInput
                value={value}
                onChange={onChange}
                currency={currency}
                disabled={false}
                controller={controller}
                onActivate={onActivate}
                minValue={stopMinAmount}
                maxValue={maxAmount}
                step={amountStep}
                useStopAmountSteps={useStopAmountSteps}
                stopAmountBaseValues={stopAmountBaseValues}
              />
            </div>
          </div>
        </div>
      );
    }

    return (
      <div
        style={{
          ...styles.section,
          ...(isSkyxMobileStop ? styles.sectionMobile : {}),
        }}
      >
        {isSkyx ? (
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: '8px',
              width: '100%',
            }}
          >
            <div
              style={
                isSkyxMobileStop
                  ? (styles.stopSectionInlineRowMobile ??
                    styles.stopSectionInlineRow ?? {
                      ...styles.stopSectionRow,
                      alignItems: 'center',
                    })
                  : (styles.stopSectionInlineRow ?? {
                      ...styles.stopSectionRow,
                      alignItems: 'center',
                    })
              }
            >
              <label style={{ ...styles.switch, flexShrink: 0 }}>
                <input
                  type="checkbox"
                  checked={isToggleOn}
                  onChange={onToggle}
                  aria-label={t(titleKey)}
                  style={{ opacity: 0, width: 0, height: 0 }}
                />
                <span
                  style={{
                    ...styles.slider,
                    ...(isToggleOn ? styles.sliderChecked : {}),
                  }}
                >
                  <span
                    style={{
                      ...styles.sliderBefore,
                      ...(isToggleOn ? styles.sliderBeforeChecked : {}),
                    }}
                  />
                </span>
              </label>
              <span
                style={{
                  ...styles.stopSectionTitle,
                  ...(isToggleOn
                    ? (styles.toggleLabelOn ?? styles.toggleLabel)
                    : (styles.toggleLabelOff ?? styles.toggleLabel)),
                  marginBottom: 0,
                  ...(isSkyxMobileStop
                    ? styles.stopSectionTitleMobile
                    : {
                        flex: '1 1 auto',
                        minWidth: 0,
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                      }),
                }}
              >
                {t(titleKey)}
              </span>
              <div
                style={{
                  ...styles.stopSectionInput,
                  marginTop: 0,
                  flexShrink: 0,
                  minWidth: 0,
                  ...(isSkyxMobileStop ? { justifySelf: 'end' } : {}),
                }}
              >
                <NumberInput
                  value={value}
                  onChange={onChange}
                  currency={currency}
                  disabled={!isEnabled}
                  controller={controller}
                  onActivate={onActivate}
                  minValue={stopMinAmount}
                  maxValue={maxAmount}
                  step={amountStep}
                  compactLayout={isSkyxMobileStop}
                  useStopAmountSteps={useStopAmountSteps}
                  stopAmountBaseValues={stopAmountBaseValues}
                />
              </div>
            </div>
            {descriptionKey ? (
              <p
                style={
                  isSkyxMobileStop
                    ? styles.stopSectionDescriptionBelow
                    : styles.stopSectionDescription
                }
              >
                {t(descriptionKey)}
              </p>
            ) : null}
          </div>
        ) : (
          <div style={styles.toggleRow}>
            <label style={styles.switch}>
              <input
                type="checkbox"
                checked={isToggleOn}
                onChange={onToggle}
                aria-label={t(titleKey)}
                style={{ opacity: 0, width: 0, height: 0 }}
              />
              <span
                style={{
                  ...styles.slider,
                  ...(isToggleOn ? styles.sliderChecked : {}),
                }}
              >
                <span
                  style={{
                    ...styles.sliderBefore,
                    ...(isToggleOn
                      ? styles.sliderBeforeChecked
                      : { opacity: 0.5 }),
                  }}
                />
              </span>
            </label>
            <span
              style={{
                ...styles.toggleLabel,
                ...(isToggleOn ? {} : { opacity: 0.5 }),
              }}
            >
              {t(titleKey)}
            </span>
            <NumberInput
              value={value}
              onChange={onChange}
              currency={currency}
              disabled={!isEnabled}
              controller={controller}
              onActivate={onActivate}
              minValue={stopMinAmount}
              maxValue={maxAmount}
              step={amountStep}
              useStopAmountSteps={useStopAmountSteps}
              stopAmountBaseValues={stopAmountBaseValues}
            />
          </div>
        )}
      </div>
    );
  };

  const modalTree = (
    <div
      ref={overlayScrollRef}
      style={{
        ...styles.overlay,
        ...(viewportBox
          ? {
              top: viewportBox.top,
              left: viewportBox.left,
              right: 'auto',
              bottom: 'auto',
              width: viewportBox.width,
              height: viewportBox.height,
              maxHeight: viewportBox.height,
            }
          : null),
        // Overlay fade in/out
        opacity: isAnimatingIn ? 1 : 0,
        transition: `opacity ${ANIMATION_DURATION}ms ease-out`,
      }}
      className="autoplay-popup"
      data-autoplay-modal="true"
      data-skyx-modal-backdrop
      onClick={handleOverlayClick}
    >
      <div
        style={getModalStyle()} // Uses the dynamic style for sliding
        className="autoplay-popup-dialog"
        onClick={e => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="autoplay-modal-title"
      >
        {isSkyx && styles.modalKeyframes && (
          <style>{styles.modalKeyframes}</style>
        )}
        <div style={styles.header} data-autoplay-header="true">
          {isSkyx ? (
            <div
              style={
                styles.headerTextGroup ?? {
                  display: 'flex',
                  flexDirection: 'column',
                  minWidth: 0,
                }
              }
            >
              <h2
                id="autoplay-modal-title"
                style={{
                  ...styles.title,
                  ...(deviceType === 'mobile'
                    ? (styles.titleMobile ?? {})
                    : {}),
                }}
              >
                {t('ui.popuppanel.autoplay.options')}
              </h2>
            </div>
          ) : (
            <h2 id="autoplay-modal-title" style={styles.title}>
              {t('ui.popuppanel.autoplay.options')}
            </h2>
          )}
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '10px',
              marginLeft: 'auto',
            }}
          >
            <WalletBalance variant="modal" />
            <button
              className={isSkyx ? 'autoplay-3d-btn' : undefined}
              style={{
                ...styles.closeButton,
                position: 'static',
                top: undefined,
                right: undefined,
                marginLeft: 0,
                ...(isSkyx && deviceType === 'mobile'
                  ? (styles.closeButtonMobile ?? {})
                  : {}),
                ...(isSkyx && isCloseHovered
                  ? (styles.closeButtonHover ?? {})
                  : {}),
              }}
              onClick={onClose}
              onMouseEnter={() => setIsCloseHovered(true)}
              onMouseLeave={() => {
                setIsCloseHovered(false);
                setIsCloseActive(false);
              }}
              onMouseDown={() => setIsCloseActive(true)}
              onMouseUp={() => setIsCloseActive(false)}
              aria-label={t('ui.common.close')}
            >
              {styles.closeIcon ? (
                <span
                  style={{
                    ...styles.closeIcon,
                    transition: 'transform 0.1s ease, opacity 0.1s ease',
                    transform: isCloseActive ? 'scale(0.9)' : 'scale(1)',
                    opacity: isCloseHovered ? 0.85 : 1,
                  }}
                  aria-hidden
                >
                  ×
                </span>
              ) : (
                <img
                  src={closeButtonImgSrc}
                  alt={t('ui.common.close')}
                  style={{
                    display: 'block',
                    width: '100%',
                    height: 'auto',
                    transition: 'transform 0.1s ease',
                    transform: isCloseActive ? 'scale(0.9)' : 'scale(1)',
                  }}
                />
              )}
            </button>
          </div>
        </div>

        {/* ── ADDED ERROR BLOCK ── */}
        {error && (
          <div
            ref={errorBannerRef}
            style={styles.errorContainer}
            role="alert"
            aria-live="polite"
          >
            <span style={styles.errorMessage}>{error}</span>
            {error !== insufficientBalanceMessage && (
              <button
                className={isSkyx ? 'autoplay-3d-btn' : undefined}
                style={{
                  ...styles.errorCloseButton,
                  ...(isSkyx
                    ? hoveredErrorClose
                      ? (styles.errorCloseButtonHover ?? {})
                      : {}
                    : {
                        textShadow: hoveredErrorClose
                          ? '0 0 8px #fff'
                          : styles.errorCloseButton.textShadow,
                      }),
                  transition: 'all 0.2s ease',
                }}
                onClick={() => setError(null)}
                onMouseEnter={() => {
                  setHoveredErrorClose(true);
                }}
                onMouseLeave={() => {
                  setHoveredErrorClose(false);
                }}
              >
                &times;
              </button>
            )}
          </div>
        )}
        {/* ── END ERROR BLOCK ── */}

        <div
          style={styles.contentArea}
          className="autoplay-popup-content bet-list-scrollbar"
          data-autoplay-scroll="true"
        >
          {/* ── Number of Rounds ── */}
          <div
            style={{
              ...styles.section,
              ...styles.sectionRounds,
              ...(isSkyx && deviceType === 'mobile'
                ? styles.sectionMobile
                : {}),
            }}
          >
            <label
              style={{
                ...styles.centeredLabel,
                ...(isSkyx && deviceType === 'mobile'
                  ? (styles.centeredLabelMobile ?? {})
                  : {}),
              }}
            >
              {t('ui.popuppanel.autoplay.number_of_rounds')}
            </label>
            <div
              style={{
                ...styles.roundsContainer,
                ...(isSkyx && deviceType === 'mobile'
                  ? (styles.roundsContainerMobile ?? {})
                  : {}),
              }}
            >
              {roundOptions.map(option => (
                <RoundOptionButton
                  key={option}
                  option={option}
                  isActive={rounds === option && isRoundsActive}
                  isHovered={hoveredRound === option}
                  isSkyx={isSkyx}
                  isMobile={deviceType === 'mobile'}
                  styles={styles}
                  onSelect={() => {
                    setRounds(option);
                    setIsRoundsActive(true);
                  }}
                  onHoverChange={hovered =>
                    setHoveredRound(hovered ? option : null)
                  }
                />
              ))}
            </div>
          </div>

          {/* ── Stop if cash decreases ── */}
          {renderStopLimitSection(
            'ui.popuppanel.autoplay.stop_if_cash_decreases_by',
            'ui.popuppanel.autoplay.stop_loss_description',
            decreaseValue,
            setDecreaseValue,
            isDecreaseStop,
            handleDecreaseToggle,
            isDecreaseStop,
            stopMaxLossAmount,
            () => setIsDecreaseStop(true)
          )}

          {/* ── Stop if cash increases ── */}
          {renderStopLimitSection(
            'ui.popuppanel.autoplay.stop_if_cash_increases_by',
            'ui.popuppanel.autoplay.stop_profit_description',
            increaseValue,
            setIncreaseValue,
            isIncreaseStop,
            handleIncreaseToggle,
            isIncreaseStop,
            stopMaxProfitAmount,
            () => setIsIncreaseStop(true)
          )}

          {/* ── Stop if single win exceeds ── */}
          {renderStopLimitSection(
            'ui.popuppanel.autoplay.stop_if_single_win_exceeds',
            'ui.popuppanel.autoplay.stop_single_win_description',
            winValue,
            setWinValue,
            isWinStop,
            handleWinToggle,
            isWinStop,
            stopMaxSingleWinAmount,
            () => setIsWinStop(true)
          )}
        </div>

        {/* ── Footer with subtle hover ── */}

        <div
          style={{
            ...styles.footer,
            ...(isSkyx && deviceType === 'mobile'
              ? (styles.footerMobile ?? {})
              : {}),
          }}
          data-autoplay-footer="true"
        >
          <button
            className={isSkyx ? 'autoplay-3d-btn' : undefined}
            style={{
              ...styles.resetButton,
              transition: 'background 0.2s ease',
              ...(isSkyx
                ? {
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    outline: 'none',
                    // No boxShadow here — resetButton sets none of its own,
                    // so leaving this key out lets the generic
                    // .autoplay-3d-btn CSS class's bevel/press-inset show
                    // through instead of an inline 'none' blocking it.
                    ...(deviceType === 'mobile'
                      ? (styles.resetButtonMobile ?? {})
                      : {}),
                    ...(hoveredReset ? (styles.resetButtonHover ?? {}) : {}),
                  }
                : {
                    backgroundColor:
                      pressedReset &&
                      (deviceType === 'mobile' ||
                        (deviceType === 'tablet' && isLandscape))
                        ? '#e0a800'
                        : hoveredReset
                          ? '#f58708'
                          : '#d07206',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    position: 'relative',
                    overflow: 'hidden',
                    boxShadow: pressedReset ? '0 0 0 0.2rem #deaa0c80' : 'none',
                    outline: 'none',
                    border: pressedReset ? 'none' : '1px solid #ffbd71',
                  }),
            }}
            onClick={handleReset}
            onMouseEnter={() => setHoveredReset(true)}
            onMouseLeave={() => {
              setHoveredReset(false);
              handlePressEndReset();
            }}
            onMouseDown={handlePressStartReset}
            onMouseUp={handlePressEndReset}
            onTouchStart={handlePressStartReset}
            onTouchEnd={handlePressEndReset}
            onTouchCancel={handlePressEndReset}
            aria-label={t('ui.popuppanel.autoplay.reset_aria')}
          >
            <span
              style={{
                display: 'inline-block',
                transform: 'translate(-1px, -0.7px)',
                transition: 'transform 0.12s ease',
                pointerEvents: 'none',
                ...(isSkyx
                  ? { color: styles.resetButton.color }
                  : {
                      color:
                        pressedReset &&
                        (deviceType === 'mobile' ||
                          (deviceType === 'tablet' && isLandscape))
                          ? '#000'
                          : '#fff',
                    }),
              }}
            >
              {t('ui.popuppanel.autoplay.reset')}
            </span>
          </button>
          <button
            type="button"
            className={isSkyx ? 'autoplay-3d-btn' : undefined}
            {...(isSkyx
              ? {
                  'data-skyx-click-sound': 'off',
                  'data-autoplay-start': 'true',
                }
              : {})}
            style={{
              ...styles.startButton,
              transition: isSkyx
                ? 'background 0.2s ease, filter 0.2s ease, box-shadow 0.2s ease, transform 0.2s ease'
                : 'background 0.2s ease, filter 0.2s ease',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              ...(hasInsufficientBalance
                ? { opacity: 0.45, cursor: 'not-allowed' }
                : {}),
              ...(isSkyx
                ? {
                    outline: 'none',
                    position: 'relative',
                    ...(deviceType === 'mobile'
                      ? (styles.startButtonMobile ?? {})
                      : {}),
                    // No inline box-shadow here — .autoplay-3d-btn owns
                    // box-shadow (rest bevel / hover / press-inset) via CSS.
                    // The purple CTA glow on hover/press is rendered by a
                    // separate ::after layer (data-autoplay-start selector,
                    // AutoPlayOverrides.ts) so the two don't fight over the
                    // same inline property.
                  }
                : {
                    backgroundColor:
                      pressedStart &&
                      (deviceType === 'mobile' ||
                        (deviceType === 'tablet' && isLandscape))
                        ? '#218838'
                        : hoveredStart
                          ? '#36cb12'
                          : '#28a909',
                    position: 'relative',
                    overflow: 'hidden',
                    boxShadow: pressedStart ? '0 0 0 0.2rem #48b46180' : 'none',
                    outline: 'none',
                    border: pressedStart ? 'none' : 'solid 1px #b2f2a3',
                  }),
            }}
            onClick={handleStart}
            aria-disabled={hasInsufficientBalance}
            onMouseEnter={() =>
              !hasInsufficientBalance && setHoveredStart(true)
            }
            onMouseLeave={() => {
              setHoveredStart(false);
              handlePressEndStart();
            }}
            onMouseDown={handlePressStartStart}
            onMouseUp={handlePressEndStart}
            onTouchStart={handlePressStartStart}
            onTouchEnd={handlePressEndStart}
            onTouchCancel={handlePressEndStart}
            onKeyDown={(e: React.KeyboardEvent) => {
              // Support keyboard press visual
              if (e.key === ' ' || e.key === 'Enter') {
                e.preventDefault();
                setPressedStart(true);
              }
              if (e.key === 'Enter') {
                handleStart();
              }
            }}
            onKeyUp={(e: React.KeyboardEvent) => {
              if (e.key === ' ' || e.key === 'Enter') {
                setPressedStart(false);
              }
            }}
            aria-label={t('ui.popuppanel.autoplay.start_aria')}
            title={
              hasInsufficientBalance ? insufficientBalanceMessage : undefined
            }
          >
            <span
              style={{
                display: 'inline-block',
                transform: 'translate(-1px, -0.7px)',
                transition: 'transform 0.12s ease',
                pointerEvents: 'none',
                color:
                  pressedStart &&
                  (deviceType === 'mobile' ||
                    (deviceType === 'tablet' && isLandscape))
                    ? '#fff'
                    : '#fff',
              }}
            >
              {t('ui.popuppanel.autoplay.start')}
            </span>
          </button>
        </div>
      </div>
    </div>
  );

  if (typeof document !== 'undefined' && document.body) {
    return createPortal(modalTree, document.body);
  }

  return modalTree;
};
